import React, { useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { PopupContainer, PopupContent, PopupHeader, CloseButton, Title, TitleIcon, ReasonHeading, FormSection, CheckboxGroup, CheckboxItem, Checkbox, CheckboxLabel, TextArea, TextAreaLabel, SendButton, ErrorMessage as ErrorMessageStyled } from './style';
import { ExclamationIcon } from './icons';
import ReportIssuePopupResult from '../ReportIssuePopupResult';
import { reportIssue } from '../../service';
import { reportIssueReasonsList } from './constants';
import { getUserRole } from '../../userLocalDetails';

const validationSchema = Yup.object({
  selectedReasons: Yup.array()
    .min(1, 'Please select at least one issue reason')
    .required('Please select an issue reason'),
  issueDescription: Yup.string()
    .min(10, 'Please provide more details (at least 10 characters)')
    .required('Please explain your issue in detail')
    .max(500, 'Please provide less than 500 characters'),
});

const ReportIssuePopup = ({ onClose }) => {
  const [resultState, setResultState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const userRole = getUserRole();
  const reasons = reportIssueReasonsList;
  const modalRef = useRef(null);
  const firstCheckboxRef = useRef(null);
  const closeButtonRef = useRef(null);
  const sendButtonRef = useRef(null);

  const setInitialFocus = (element) => {
    if (element && !document.activeElement || document.activeElement === document.body) {
      element.focus();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    
    if (event.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  const handleModalClick = (event) => {
    if (event.target === modalRef.current) onClose();
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);
      
      const response = await reportIssue(values.selectedReasons, values.issueDescription, userRole);
      
      if (response) {
        setResultState('success');
      } else {
        throw new Error('Failed to submit report');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      setResultState('failure');
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setResultState(null);
    onClose();
  };

  if (resultState) {
    return <ReportIssuePopupResult onClose={handleClose} isSuccess={resultState === 'success'} />;
  }

  return (
    <PopupContainer
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      onKeyDown={handleKeyDown}
      onClick={handleModalClick}
    >
      <PopupContent ref={modalRef} onFocus={() => setInitialFocus(firstCheckboxRef.current)}>
        <PopupHeader>
          <Title id="modal-title">
            <TitleIcon>
              <ExclamationIcon />
            </TitleIcon>
            Report an issue
          </Title>
          <CloseButton 
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </CloseButton>
        </PopupHeader>
        
        <Formik
          initialValues={{
            selectedReasons: [],
            issueDescription: ''
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched, isSubmitting }) => (
            <Form>
              <ReasonHeading id="reason-heading">Reason</ReasonHeading>
              
              <FormSection>
                <CheckboxGroup role="group" aria-labelledby="reason-heading">
                  {reasons.map((reason, index) => (
                    <CheckboxItem key={index}>
                      <Checkbox
                        type="checkbox"
                        id={`reason-${index}`}
                        ref={index === 0 ? firstCheckboxRef : null}
                        checked={values.selectedReasons.includes(reason.key)}
                        onChange={() => {
                          const newReasons = values.selectedReasons.includes(reason.key)
                            ? values.selectedReasons.filter(r => r !== reason.key)
                            : [...values.selectedReasons, reason.key];
                          setFieldValue('selectedReasons', newReasons);
                        }}
                        aria-describedby={errors.selectedReasons && touched.selectedReasons ? 'reason-error' : undefined}
                        autoFocus={index === 0}
                      />
                      <CheckboxLabel htmlFor={`reason-${index}`}>
                        {reason.text}
                      </CheckboxLabel>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
                {errors.selectedReasons && touched.selectedReasons && (
                  <ErrorMessageStyled id="reason-error" role="alert">
                    {errors.selectedReasons}
                  </ErrorMessageStyled>
                )}
                
                <TextAreaLabel htmlFor="issue-description">
                  Explain your issue in more detail
                </TextAreaLabel>
                <Field
                  as={TextArea}
                  id="issue-description"
                  name="issueDescription"
                  placeholder="Start typing here..."
                  aria-describedby={errors.issueDescription && touched.issueDescription ? 'description-error' : undefined}
                  aria-required="true"
                />
                <ErrorMessage 
                  name="issueDescription" 
                  component={({ children }) => (
                    <ErrorMessageStyled id="description-error" role="alert">
                      {children}
                    </ErrorMessageStyled>
                  )} 
                />
                
                <SendButton 
                  ref={sendButtonRef}
                  type="submit" 
                  disabled={isLoading || isSubmitting}
                >
                  {isLoading ? 'Sending...' : 'Send Report'}
                </SendButton>
              </FormSection>
            </Form>
          )}
        </Formik>
      </PopupContent>
    </PopupContainer>
  );
};

export default ReportIssuePopup;
